import * as React from 'react'

import { arrayMoveImmutable } from 'array-move'
import { Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react'
import {
  SortableContainer,
  SortableElement,
  SortEnd,
  SortEvent,
  SortEventWithTag,
} from 'react-sortable-hoc'

import { cn } from '@/lib/utils'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import OptionsBody from '~/client/src/shared/components/OptionsList/pieces/OptionsBody'
import OptionsHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsHeader'
import OptionsPanel from '~/client/src/shared/components/OptionsList/pieces/OptionsPanel'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IViewOptionsColumnItem } from './types'

type ColumnSection = 'fixed' | 'shown' | 'hidden'

type SortableEntry =
  | {
      type: 'shown-header'
      key: string
      shownCount: number
    }
  | {
      type: 'item'
      key: string
      item: IViewOptionsColumnItem
      section: ColumnSection
    }
  | {
      type: 'freeze-divider'
      key: string
    }
  | {
      type: 'hidden-separator'
      key: string
    }
  | {
      type: 'hidden-header'
      key: string
      hiddenCount: number
    }

interface IColumnSections {
  fixedItems: IViewOptionsColumnItem[]
  shownItems: IViewOptionsColumnItem[]
  hiddenItems: IViewOptionsColumnItem[]
}

interface IProps {
  title: string
  items: IViewOptionsColumnItem[]
  onChange: (items: IViewOptionsColumnItem[]) => void
  onBack: () => void
  onReset?: () => void
  onClose?: () => void
}

interface IColumnConfigRowProps {
  item: IViewOptionsColumnItem
  section: ColumnSection
  onToggleHidden: (key: string) => void
}

interface ISortableColumnListProps {
  entries: SortableEntry[]
  onToggleHidden: (key: string) => void
  onReset?: () => void
}

interface ISortableEntryProps {
  entry: SortableEntry
  onToggleHidden: (key: string) => void
  onReset?: () => void
}

function getColumnSection(item: IViewOptionsColumnItem): ColumnSection {
  if (item.isHidden) return 'hidden'
  if (item.isFixed) return 'fixed'
  return 'shown'
}

function matchesSearch(item: IViewOptionsColumnItem, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  return item.label.toLowerCase().includes(normalizedQuery)
}

function buildSections(
  items: IViewOptionsColumnItem[],
  query: string,
): IColumnSections {
  return items
    .filter(item => matchesSearch(item, query))
    .reduce<IColumnSections>(
      (sections, item) => {
        switch (getColumnSection(item)) {
          case 'fixed':
            sections.fixedItems.push(item)
            break
          case 'hidden':
            sections.hiddenItems.push(item)
            break
          case 'shown':
          default:
            sections.shownItems.push(item)
            break
        }

        return sections
      },
      { fixedItems: [], shownItems: [], hiddenItems: [] },
    )
}

function getRenderedItems(sections: IColumnSections): IViewOptionsColumnItem[] {
  return [
    ...sections.fixedItems,
    ...sections.shownItems,
    ...sections.hiddenItems,
  ]
}

function buildEntries(sections: IColumnSections): SortableEntry[] {
  const entries: SortableEntry[] = [
    {
      type: 'shown-header',
      key: 'shown-header',
      shownCount: sections.fixedItems.length + sections.shownItems.length,
    },
    ...sections.fixedItems.map(item => ({
      type: 'item' as const,
      key: item.key,
      item,
      section: 'fixed' as const,
    })),
    { type: 'freeze-divider', key: 'freeze-divider' },
    ...sections.shownItems.map(item => ({
      type: 'item' as const,
      key: item.key,
      item,
      section: 'shown' as const,
    })),
  ]

  if (sections.hiddenItems.length > 0) {
    entries.push(
      { type: 'hidden-separator', key: 'hidden-separator' },
      {
        type: 'hidden-header',
        key: 'hidden-header',
        hiddenCount: sections.hiddenItems.length,
      },
      ...sections.hiddenItems.map(item => ({
        type: 'item' as const,
        key: item.key,
        item,
        section: 'hidden' as const,
      })),
    )
  }

  return entries
}

function normalizeColumns(
  items: IViewOptionsColumnItem[],
): IViewOptionsColumnItem[] {
  return [
    ...items.filter(item => item.isFixed && !item.isHidden),
    ...items.filter(item => !item.isFixed && !item.isHidden),
    ...items.filter(item => item.isHidden),
  ]
}

function applySection(
  item: IViewOptionsColumnItem,
  section: ColumnSection,
): IViewOptionsColumnItem {
  switch (section) {
    case 'fixed':
      return { ...item, isFixed: true, isHidden: false }
    case 'hidden':
      return { ...item, isFixed: false, isHidden: true }
    case 'shown':
    default:
      return { ...item, isFixed: false, isHidden: false }
  }
}

function getMovedVisibleItems(
  entries: SortableEntry[],
): IViewOptionsColumnItem[] {
  let section: ColumnSection = 'fixed'

  return entries.reduce<IViewOptionsColumnItem[]>((items, entry) => {
    switch (entry.type) {
      case 'freeze-divider':
        section = 'shown'
        return items
      case 'hidden-header':
        section = 'hidden'
        return items
      case 'item':
        items.push(applySection(entry.item, section))
        return items
      default:
        return items
    }
  }, [])
}

function moveColumn(
  items: IViewOptionsColumnItem[],
  query: string,
  oldIndex: number,
  newIndex: number,
): IViewOptionsColumnItem[] {
  const fullItems = getRenderedItems(buildSections(items, ''))
  const visibleEntries = buildEntries(buildSections(items, query))
  const movedEntry = visibleEntries[oldIndex]

  if (!movedEntry || movedEntry.type !== 'item') return items

  const reorderedVisibleEntries = arrayMoveImmutable(
    visibleEntries,
    oldIndex,
    newIndex,
  )
  const visibleItems = getMovedVisibleItems(reorderedVisibleEntries)
  const visibleKeys = new Set(
    visibleEntries
      .filter(entry => entry.type === 'item')
      .map(entry => entry.item.key),
  )
  let visibleIndex = 0

  return normalizeColumns(
    fullItems.map(item => {
      if (!visibleKeys.has(item.key)) return item

      const nextItem = visibleItems[visibleIndex]
      visibleIndex += 1
      return nextItem
    }),
  )
}

function toggleHidden(
  items: IViewOptionsColumnItem[],
  key: string,
): IViewOptionsColumnItem[] {
  const currentIndex = items.findIndex(item => item.key === key)
  if (currentIndex === -1) return items

  let firstHiddenIndex = items.map(item => !!item.isHidden).indexOf(true)
  if (firstHiddenIndex === -1) firstHiddenIndex = items.length

  const newIndex =
    currentIndex < firstHiddenIndex ? firstHiddenIndex - 1 : firstHiddenIndex
  const collection = arrayMoveImmutable(items, currentIndex, newIndex)

  collection[newIndex] = {
    ...collection[newIndex],
    isHidden: !collection[newIndex].isHidden,
  }

  return normalizeColumns(collection)
}

function shouldCancelSortStart(event: SortEvent | SortEventWithTag): boolean {
  const target = event.target
  return target instanceof HTMLElement && !!target.closest('button, input')
}

const ColumnConfigRow: React.FC<IColumnConfigRowProps> = ({
  item,
  section,
  onToggleHidden,
}) => {
  const isHidden = section === 'hidden'

  return (
    <div
      className={cn(
        'tw:flex tw:h-9 tw:select-none tw:items-center tw:gap-2 tw:rounded-md tw:px-1.5 tw:text-app-dense tw:transition-colors tw:cursor-grab tw:active:cursor-grabbing',
        isHidden
          ? 'tw:text-muted-foreground/70'
          : 'tw:text-foreground tw:hover:bg-accent',
      )}
    >
      <GripVertical className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
      {item.icon && (
        <span className="tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:text-muted-foreground">
          {item.icon}
        </span>
      )}
      <span className="tw:min-w-0 tw:flex-1 tw:truncate">{item.label}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={
          isHidden
            ? Localization.translator.showObject
            : Localization.translator.hide
        }
        onClick={() => onToggleHidden(item.key)}
      >
        {isHidden ? <EyeOff /> : <Eye />}
      </Button>
    </div>
  )
}

function ColumnConfigEntry({
  entry,
  onToggleHidden,
  onReset,
}: ISortableEntryProps) {
  switch (entry.type) {
    case 'shown-header':
      return (
        <div className="tw:flex tw:h-8 tw:select-none tw:items-center tw:gap-2 tw:px-1.5 tw:text-app-dense tw:text-muted-foreground">
          <span className="tw:min-w-0 tw:flex-1 tw:truncate">
            {Localization.translator.shownInTable} ({entry.shownCount})
          </span>
          {onReset && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={Localization.translator.restoreDefaults}
              onClick={onReset}
            >
              <RotateCcw />
            </Button>
          )}
        </div>
      )
    case 'freeze-divider':
      return (
        <div className="tw:flex tw:h-8 tw:select-none tw:items-center tw:gap-2 tw:text-app-dense tw:text-muted-foreground">
          <Separator className="tw:flex-1" />
          <span className="tw:shrink-0">
            {Localization.translator.freezeColumns}
          </span>
          <Separator className="tw:flex-1" />
        </div>
      )
    case 'hidden-separator':
      return (
        <div className="tw:flex tw:h-3 tw:select-none tw:items-center">
          <Separator />
        </div>
      )
    case 'hidden-header':
      return (
        <div className="tw:flex tw:h-8 tw:select-none tw:items-center tw:px-1.5 tw:text-app-dense tw:text-muted-foreground">
          {Localization.translator.hiddenInTable} ({entry.hiddenCount})
        </div>
      )
    case 'item':
    default:
      return (
        <ColumnConfigRow
          item={entry.item}
          section={entry.section}
          onToggleHidden={onToggleHidden}
        />
      )
  }
}

const SortableColumnConfigEntry = SortableElement<ISortableEntryProps>(
  props => (
    <div className="tw:w-full tw:z-[120] tw:select-none">
      <ColumnConfigEntry {...props} />
    </div>
  ),
)

const SortableColumnList = SortableContainer<ISortableColumnListProps>(
  ({ entries, onToggleHidden, onReset }) => {
    return (
      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:select-none tw:flex-col tw:overflow-y-auto">
        {entries.map((entry, index) => (
          <SortableColumnConfigEntry
            key={entry.key}
            index={index}
            disabled={entry.type !== 'item'}
            entry={entry}
            onToggleHidden={onToggleHidden}
            onReset={onReset}
          />
        ))}
      </div>
    )
  },
)

const ColumnConfigPanel: React.FC<IProps> = ({
  title,
  items,
  onChange,
  onBack,
  onReset,
  onClose,
}) => {
  const [query, setQuery] = React.useState('')

  const entries = React.useMemo(
    () => buildEntries(buildSections(items, query)),
    [items, query],
  )

  const handleSortEnd = React.useCallback(
    ({ oldIndex, newIndex }: SortEnd) => {
      onChange(moveColumn(items, query, oldIndex, newIndex))
    },
    [items, onChange, query],
  )

  const handleToggleHidden = React.useCallback(
    (key: string) => {
      onChange(toggleHidden(items, key))
    },
    [items, onChange],
  )

  return (
    <OptionsPanel>
      <OptionsHeader title={title} onBack={onBack} onClose={onClose} />
      <OptionsBody>
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3">
          <SearchInput
            value={query}
            onChange={setQuery}
            debounceMs={0}
            className="tw:w-full"
          />

          <SortableColumnList
            entries={entries}
            onToggleHidden={handleToggleHidden}
            onReset={onReset}
            onSortEnd={handleSortEnd}
            axis="y"
            lockAxis="y"
            distance={2}
            shouldCancelStart={shouldCancelSortStart}
            helperClass="tw:z-[120] tw:select-none tw:rounded-md tw:bg-popover tw:shadow-md"
          />
        </div>
      </OptionsBody>
    </OptionsPanel>
  )
}

export default ColumnConfigPanel
